import { useNavigate } from "react-router-dom";
import { useState } from "react";
import { Home, PlusCircle, Stethoscope, Compass, User, ChevronLeft, Bell, CheckCheck } from "lucide-react";
import {
  useCareNotificationPoller, useUnreadCount, useNotifications,
  markAllNotificationsRead, markCaseSeen,
} from "@/lib/careNotify";

const TABS = [
  { key: "home", label: "Home", icon: Home, path: "/care" },
  { key: "new", label: "New", icon: PlusCircle, path: "/care/new" },
  { key: "cases", label: "My Cases", icon: Stethoscope, path: "/care/cases" },
  { key: "lifestyle", label: "Lifestyle", icon: Compass, path: "/" },
  { key: "account", label: "Account", icon: User, path: "/care/account" },
];

function timeAgo(iso) {
  const s = Math.floor((Date.now() - new Date(iso).getTime()) / 1000);
  if (s < 60) return "just now";
  if (s < 3600) return `${Math.floor(s / 60)}m ago`;
  if (s < 86400) return `${Math.floor(s / 3600)}h ago`;
  return `${Math.floor(s / 86400)}d ago`;
}

export function CareShell({ active, title, showBack = false, onBack, children }) {
  const navigate = useNavigate();
  const unread = useUnreadCount();
  const feed = useNotifications();
  const [notifOpen, setNotifOpen] = useState(false);
  useCareNotificationPoller(navigate);

  const openNotification = (n) => {
    markCaseSeen(n.caseId);
    setNotifOpen(false);
    navigate(`/care/cases/${n.caseId}`);
  };

  return (
    <div className="care-scope min-h-screen w-full flex justify-center bg-black">
      <div className="relative w-full max-w-[430px] h-screen bg-[#111111] shadow-2xl flex flex-col overflow-hidden">
        <header className="relative z-40 bg-[#111111] border-b border-[#333333] px-4 py-4 flex items-center justify-between gap-3 shrink-0">
          <div className="flex items-center gap-2 min-w-0">
            {showBack && (
              <button
                data-testid="care-back-button"
                aria-label="Go back"
                onClick={onBack || (() => navigate(-1))}
                className="min-h-[44px] min-w-[44px] -ml-2 flex items-center justify-center rounded-xl text-[#F8FAFC] hover:bg-white/5 transition-colors"
              >
                <ChevronLeft size={26} />
              </button>
            )}
            <h1 className="font-head text-xl font-bold text-[#F8FAFC] truncate">{title}</h1>
          </div>

          <div className="flex items-center gap-2 shrink-0">
            <button
              data-testid="care-notifications-btn"
              aria-label={unread > 0 ? `Notifications, ${unread} unread` : "Notifications"}
              onClick={() => setNotifOpen((o) => !o)}
              className="relative min-h-[44px] min-w-[44px] flex items-center justify-center rounded-full text-[#F8FAFC] hover:bg-white/5 transition-colors"
            >
              <Bell size={20} />
              {unread > 0 && (
                <span
                  data-testid="care-notifications-badge"
                  className="absolute top-1.5 right-1.5 min-w-[16px] h-[16px] px-1 rounded-full bg-[#EF9F27] text-[#111111] text-[10px] font-extrabold flex items-center justify-center"
                >
                  {unread > 9 ? "9+" : unread}
                </span>
              )}
            </button>
          </div>

          {notifOpen && (
            <>
              <button
                aria-label="Close notifications"
                onClick={() => setNotifOpen(false)}
                className="fixed inset-0 z-40 cursor-default"
                tabIndex={-1}
              />
              <div
                data-testid="care-notifications-panel"
                className="absolute right-3 top-[64px] z-50 w-[300px] max-h-[60vh] overflow-y-auto no-scrollbar bg-[#1A1A1A] border border-[#333333] rounded-2xl shadow-2xl"
              >
                <div className="flex items-center justify-between px-4 py-3 border-b border-[#333333]">
                  <p className="font-head font-bold text-[#F8FAFC]">Notifications</p>
                  {feed.some((n) => !n.read) && (
                    <button
                      data-testid="care-notifications-mark-all"
                      onClick={markAllNotificationsRead}
                      className="text-xs font-bold text-[#EF9F27] inline-flex items-center gap-1"
                    >
                      <CheckCheck size={13} /> Mark all read
                    </button>
                  )}
                </div>
                {feed.length === 0 ? (
                  <p className="px-4 py-8 text-center text-sm text-[#A1A1AA]" data-testid="care-notifications-empty">
                    No notifications yet. We'll let you know when an opinion is ready.
                  </p>
                ) : (
                  <ul>
                    {feed.map((n) => (
                      <li key={n.caseId + n.at}>
                        <button
                          data-testid={`care-notification-${n.caseId}`}
                          onClick={() => openNotification(n)}
                          className="w-full text-left px-4 py-3 border-b border-[#282828] last:border-0 hover:bg-[#222222] transition-colors flex gap-3"
                        >
                          {!n.read && <span className="mt-1.5 w-2 h-2 rounded-full bg-[#EF9F27] shrink-0" />}
                          <div className={`min-w-0 ${n.read ? "pl-5" : ""}`}>
                            <p className="text-sm font-semibold text-[#F8FAFC]">Second opinion ready</p>
                            <p className="text-sm text-[#A1A1AA] truncate">{n.title}</p>
                            <p className="text-xs text-[#666666] mt-0.5">{timeAgo(n.at)}</p>
                          </div>
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </>
          )}
        </header>

        <main className="flex-1 overflow-y-auto no-scrollbar px-5 pt-5 pb-8">
          {children}
          <footer className="mt-10 pt-6 border-t border-[#333333]">
            <p className="text-xs text-[#A1A1AA] leading-relaxed" data-testid="care-footer-disclaimer">
              PocketDocta Care provides second-opinion information for discussion with your doctor. It
              is not a diagnosis, prescription, or a substitute for in-person medical care. In an
              emergency, call your local emergency services.
            </p>
          </footer>
        </main>

        {active && (
          <nav
            aria-label="Care navigation"
            className="shrink-0 bg-[#111111] border-t border-[#333333] z-50"
          >
            <ul className="flex">
              {TABS.map((t) => {
                const Icon = t.icon;
                const isActive = active === t.key;
                const showBadge = t.key === "cases" && unread > 0;
                return (
                  <li key={t.key} className="flex-1">
                    <button
                      data-testid={`care-tab-${t.key}`}
                      aria-label={showBadge ? `${t.label}, ${unread} new` : t.label}
                      aria-current={isActive ? "page" : undefined}
                      onClick={() => navigate(t.path)}
                      className={`w-full min-h-[64px] flex flex-col items-center justify-center gap-1 transition-colors ${
                        isActive ? "text-[#EF9F27] font-bold" : "text-[#666666] font-medium hover:text-[#A1A1AA]"
                      }`}
                    >
                      <span className="relative">
                        <Icon size={24} strokeWidth={isActive ? 2.4 : 2} />
                        {showBadge && (
                          <span
                            data-testid="care-cases-badge"
                            className="absolute -top-1.5 -right-2 min-w-[16px] h-[16px] px-1 rounded-full bg-[#EF9F27] text-[#111111] text-[10px] font-extrabold flex items-center justify-center"
                          >
                            {unread > 9 ? "9+" : unread}
                          </span>
                        )}
                      </span>
                      <span className="text-[11px]">{t.label}</span>
                    </button>
                  </li>
                );
              })}
            </ul>
          </nav>
        )}
      </div>
    </div>
  );
}
